import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chamarEstoque } from '@/hooks/useStock';
import { toast } from '@/hooks/use-toast';
import { mensagemDoErro } from '@/components/stock/stockErrorMessages';

/**
 * Modo de implantação do módulo de estoque (migration 0082).
 *
 * As 7 capacidades ligam uma a uma, por equipe treinada. Enquanto uma está
 * DESLIGADA, a tela dela não monta e a rota recusa a escrita — o dia da equipe
 * é idêntico ao de antes do módulo existir.
 *
 * ⚠️ A prosa de cada capacidade (o que muda, o que passa a ser cobrado) vem do
 * SERVIDOR, não daqui: é o mesmo módulo que aplica o gate. Espelhar esse texto
 * no front criaria duas versões da mesma regra, e a errada seria justamente a
 * que o gerente lê na hora de decidir.
 */

export type StockMode = 'DESLIGADO' | 'OPCIONAL' | 'EXIGIDO';

export interface StockCapabilityInfo {
  capacidade: string;
  modo: StockMode;
  equipe: string;
  bloco: 'A' | 'B' | 'C' | 'D';
  temExigido: boolean;
  descricao: string;
  exigencia: string;
  disponivel: boolean;
  alteradoPor: string | null;
  alteradoEm: string | null;
}

export const stockCapabilityKeys = {
  all: ['stock', 'capabilities'] as const,
};

export function useStockCapabilities() {
  return useQuery({
    queryKey: stockCapabilityKeys.all,
    queryFn: () => chamarEstoque<StockCapabilityInfo[]>('/api/stock/capabilities'),
    // Config muda raramente, e quando muda o listener de realtime invalida na
    // hora — não é o staleTime que mantém isto fresco.
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Estado de UMA capacidade, para a tela que decide se monta o caminho novo.
 *
 * Enquanto carrega devolve `undefined` — quem consome trata como "ainda não
 * sei", nunca como DESLIGADO: um piscar do card antigo para o novo no meio de
 * uma bipagem é pior que meio segundo de esqueleto.
 */
export function useStockCapability(capacidade: string): {
  modo: StockMode | undefined;
  podeUsar: boolean | undefined;
  exige: boolean | undefined;
  /** A leitura FALHOU (não "está carregando"): a tela mostra erro com retry, não esqueleto eterno. */
  erro: boolean;
  recarregar: () => void;
} {
  const { data, isError, refetch } = useStockCapabilities();
  const info = data?.find((c) => c.capacidade === capacidade);
  const recarregar = () => { void refetch(); };
  if (!data) return { modo: undefined, podeUsar: undefined, exige: undefined, erro: isError, recarregar };
  const modo = info?.modo ?? 'DESLIGADO';
  return { modo, podeUsar: modo !== 'DESLIGADO', exige: modo === 'EXIGIDO', erro: false, recarregar };
}

export function useAlterarCapacidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { capacidade: string; modo: StockMode }) =>
      chamarEstoque<{ capacidade: string; modo: StockMode }>('/api/stock/capabilities', {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: stockCapabilityKeys.all });
    },
    onError: (err: Error & { code?: string }) => {
      const m = mensagemDoErro(err.code);
      toast({ title: m.titulo, description: m.oQueFazer, variant: 'destructive' });
    },
  });
}
